import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { legalModalContent } from '../content/legalModalContent';

type FooterLinksVariant = 'home' | 'about';
type LegalModalType = 'privacy' | 'terms' | null;

interface SiteFooterLinksProps {
  variant: FooterLinksVariant;
}

const modalContent = legalModalContent;

function SiteFooterLinks({ variant }: SiteFooterLinksProps) {
  const theme = useTheme();
  const { almathema } = theme.palette;
  const location = useLocation();
  const [openModal, setOpenModal] = useState<LegalModalType>(null);

  const isHome = variant === 'home';
  const quickLinksHeadingSx = isHome
    ? { fontWeight: 700, mb: 1.5, fontStyle: 'italic' }
    : { fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 700, fontSize: '1.4rem', mb: 1.5 };
  const infoHeadingSx = isHome
    ? { fontWeight: 700, mb: 1.5, fontStyle: 'italic' }
    : { fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 700, fontSize: '1.4rem', mb: 1.5 };

  const handleHomeClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  };

  return (
    <>
      <Box sx={{ flex: 1, py: isHome ? 5 : 0 }}>
        <Box component="img" src="/logo-almathema-footer.svg" alt="alMATHEMA" sx={{ width: isHome ? 180 : 190, mb: isHome ? 1 : 1.2 }} />
        <Typography sx={{ maxWidth: isHome ? undefined : 300, fontSize: isHome ? undefined : '0.95rem', mb: 2 }} variant={isHome ? 'body2' : undefined}>
          alMATHEMA is growing through collaboration with students, educators, and institutions.
        </Typography>
        <Typography variant="caption">© 2026 ALMATHEMA INC. All rights reserved.</Typography>
      </Box>

      <Box sx={{ minWidth: isHome ? 180 : undefined, py: isHome ? 5 : 0 }}>
        <Typography variant="h6" sx={quickLinksHeadingSx}>Quick links</Typography>
        <Link
          component={RouterLink}
          to="/"
          onClick={handleHomeClick}
          underline="none"
          color="inherit"
          sx={{ display: 'block', mb: isHome ? 1.5 : 1, fontSize: isHome ? '0.875rem' : '1rem' }}
        >
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/about-us"
          underline="none"
          color="inherit"
          sx={{ display: 'block', mb: isHome ? 1.5 : 1, fontSize: isHome ? '0.875rem' : '1rem' }}
        >
          About Us
        </Link>
        <Link
          component={RouterLink}
          to="/#contact-us-section"
          underline="none"
          color="inherit"
          sx={{ display: 'block', fontSize: isHome ? '0.875rem' : '1rem' }}
        >
          Contact Us
        </Link>
      </Box>

      <Box sx={{ minWidth: isHome ? 220 : undefined, py: isHome ? 5 : 0 }}>
        <Typography variant="h6" sx={infoHeadingSx}>Information</Typography>
        <Link
          component="button"
          type="button"
          underline="none"
          color="inherit"
          onClick={() => setOpenModal('privacy')}
          sx={{
            display: 'block',
            mb: isHome ? 1.5 : 1,
            fontSize: isHome ? '0.875rem' : '1rem',
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          Privacy Policy
        </Link>
        <Link
          component="button"
          type="button"
          underline="none"
          color="inherit"
          onClick={() => setOpenModal('terms')}
          sx={{
            display: 'block',
            mb: isHome ? 1.5 : 1,
            fontSize: isHome ? '0.875rem' : '1rem',
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          Terms & Conditions
        </Link>
        <Link
          href="mailto:contact@almathema.com"
          underline="hover"
          color="inherit"
          sx={{ display: 'inline-block', fontSize: isHome ? '0.875rem' : '1rem' }}
        >
          contact@almathema.com
        </Link>
      </Box>

      <Dialog open={openModal !== null} onClose={() => setOpenModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle variant='h3'>{openModal ? modalContent[openModal].title : ''}</DialogTitle>
        <DialogContent
          sx={{
            '& .legal-markdown p': { mb: 1.5, lineHeight: 1.6 },
            '& .legal-markdown h1, & .legal-markdown h2, & .legal-markdown h3': {
              mt: 2,
              mb: 1,
              fontWeight: 700,
              lineHeight: 1.3,
            },
            '& .legal-markdown h1': { fontSize: '1.5rem' },
            '& .legal-markdown h2': { fontSize: '1.25rem' },
            '& .legal-markdown h3': { fontSize: '1.1rem' },
            '& .legal-markdown ul, & .legal-markdown ol': { pl: 3, mb: 1.5 },
            '& .legal-markdown li': { mb: 0.5 },
            '& .legal-markdown a': { color: almathema.sapphire },
          }}
        >
          <Box className="legal-markdown">
            <ReactMarkdown>{openModal ? modalContent[openModal].body : ''}</ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(null)} sx={{ color: almathema.sapphire }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SiteFooterLinks;